/**
 * A área das automações, para a organização que tem a funcionalidade (fase 2):
 * a lista das automações e a galeria de modelos, em duas abas.
 *
 * O editor do mapa e o acompanhamento entram cada um no seu passo, e até lá
 * esta tela não oferece o que leva a eles ("Editar", "Ver execuções", "Começar
 * do zero"). O que está aqui funciona de verdade: lê do serviço, cria a partir
 * de um modelo, liga, pausa, manda para teste e arquiva, e diz por que quando
 * não deu.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useAutomacoes,
  useBlocosDoEditor,
  useModelosDeAutomacao,
  usePainelDasAutomacoes,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { podeNaSessao, useSessao } from '@/shared/sessao/contexto';
import type { QuemNaTela } from '@/entities/acesso/papel';
import {
  Botao,
  CabecalhoDeTela,
  Campo,
  FiltroPilula,
  Icone,
  Interruptor,
  MenuAcoes,
  Segmentado,
  Selo,
  idDoPainel,
  type ItemMenu,
  type NomeDoIcone,
} from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { AutomacoesArquivadas } from './automacoes-arquivadas';
import { ExcluirAutomacao } from './excluir-automacao';
import {
  FILTROS,
  ROTULO_DO_COMANDO,
  SITUACAO,
  comandosDe,
  contagens,
  filtrar,
  iconeDaAutomacao,
  nomeDaAutomacao,
  numerosDoPainel,
  origemDaAutomacao,
  podeEditarMapa,
  recadoDoComando,
  resumoDoGatilho,
  temInterruptor,
  type Automacao,
  type Comando,
  type Filtro,
} from '../lib/automacoes';
import { modelosOferecidos } from '../lib/modelos';
import { GaleriaDeModelos } from './galeria-de-modelos';
import { HistoricoDaAutomacao } from './historico-da-automacao';
import { RenomearAutomacao } from './renomear-automacao';
import './area-das-automacoes.css';

/**
 * O recado some sozinho depois disto. O que tem "Desfazer" fica até a pessoa
 * fechar ou fazer outra coisa: quem chega ao botão pelo teclado não pode
 * perdê-lo no meio do caminho.
 */
const DURACAO_DO_RECADO_MS = 6_000;

type Recado = {
  texto: string;
  /** Pausar e religar se desfazem com o comando oposto; o resto não. */
  desfazer?: { automacao: Automacao; comando: Comando };
};

/** As abas da área: a lista e a galeria de modelos. */
export type PaginaDaArea = 'lista' | 'modelos';

export function AreaDasAutomacoes({
  pagina = 'lista',
  aoNavegar = () => undefined,
  aoAbrirMapa,
  aoVerExecucoes,
}: {
  pagina?: PaginaDaArea;
  aoNavegar?: (pagina: PaginaDaArea) => void;
  /**
   * Abre o mapa de uma automação; com `versao`, uma versão antiga, só para ler.
   * `novo` diz que ela acabou de ser criada do zero.
   */
  aoAbrirMapa?: (id: string, versao: number | null, novo?: boolean) => void;
  /** Abre o acompanhamento: de uma automação, ou só o que falhou. */
  aoVerExecucoes?: (automacao: string | null, falhou: boolean) => void;
}): ReactNode {
  const { estado } = useSessao();
  const me = estado.nome === 'autenticado' ? estado.me : null;
  // Quem está na tela: o papel e as permissões do `/v1/me` (#732).
  const quem = me;

  const api = useApi();
  const cache = useQueryClient();
  const emUso = useAutomacoes();
  const arquivadas = useAutomacoes('arquivada');
  const modelos = useModelosDeAutomacao();
  const blocos = useBlocosDoEditor();

  const [filtro, definirFiltro] = useState<Filtro>('todas');
  const [busca, definirBusca] = useState('');
  const [recado, definirRecado] = useState<Recado | null>(null);
  const [falha, definirFalha] = useState<string | null>(null);
  /*
   * Só o id: a automação do painel sai da lista a cada vez, para o painel ver a
   * versão e a situação de agora depois de restaurar.
   */
  const [historicoDe, definirHistoricoDe] = useState<string | null>(null);
  const [renomeando, definirRenomeando] = useState<Automacao | null>(null);
  const [vendoArquivadas, definirVendoArquivadas] = useState(false);
  const [excluindo, definirExcluindo] = useState<Automacao | null>(null);

  useEffect(() => {
    if (!recado || recado.desfazer) return;
    const prazo = window.setTimeout(() => definirRecado(null), DURACAO_DO_RECADO_MS);
    return () => window.clearTimeout(prazo);
  }, [recado]);

  const nomeDe = (a: Automacao): string => nomeDaAutomacao(a, modelos.data);

  const comando = useMutation({
    mutationFn: ({
      automacao,
      comando,
    }: {
      automacao: Automacao;
      comando: Comando;
      /** Já é o "Desfazer": não oferece desfazer o desfazer. */
      desfazendo?: boolean;
    }): Promise<unknown> => {
      // A resposta não é lida: a lista se relê no fim (`onSettled`).
      const caminho = { id: automacao.id };
      switch (comando) {
        case 'enviar':
          return api!.enviarAutomacaoParaHomologacao({ caminho });
        case 'voltar':
          return api!.voltarAutomacaoParaRascunho({ caminho });
        case 'ativar':
          return api!.ativarAutomacao({ caminho });
        case 'pausar':
          return api!.pausarAutomacao({ caminho });
        case 'arquivar':
          return api!.arquivarAutomacao({ caminho });
        case 'desarquivar':
          return api!.desarquivarAutomacao({ caminho });
        case 'excluir':
          return api!.removerAutomacao({ caminho });
      }
    },
    onMutate: () => {
      definirFalha(null);
      definirRecado(null);
    },
    onSuccess: (_resposta, { automacao, comando: feito, desfazendo }) => {
      const nome = nomeDe(automacao);
      const oposto: Comando | null = desfazendo
        ? null
        : feito === 'pausar'
          ? 'ativar'
          : feito === 'ativar' && automacao.situacao === 'pausada'
            ? 'pausar'
            : null;
      definirRecado({
        texto: recadoDoComando(feito, nome, automacao.situacao),
        ...(oposto
          ? {
              desfazer: {
                automacao: { ...automacao, situacao: feito === 'pausar' ? 'pausada' : 'ativa' },
                comando: oposto,
              },
            }
          : {}),
      });
    },
    onError: (erro) => {
      definirFalha(
        recadoDeErro(erro, {
          generica: 'Não foi possível mudar a automação agora. Nada mudou; tente de novo.',
          porCodigo: {
            ENGINE_INDISPONIVEL:
              'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
            CONFLITO_DE_ESTADO:
              'A automação mudou enquanto a tela estava aberta. A lista foi atualizada: confira e tente de novo.',
            REQUISICAO_INVALIDA:
              'O serviço recusou a automação como ela está. Abra e corrija antes de tentar de novo.',
            NAO_ENCONTRADO: 'Esta automação não existe mais. A lista foi atualizada.',
            SEM_PERMISSAO: 'O seu papel não pode fazer isso nesta automação.',
          },
        }),
      );
    },
    // Deu certo ou não, a lista volta a ser a do serviço: num 409, é ela que
    // mostra a situação de agora.
    onSettled: () => void cache.invalidateQueries({ queryKey: chaves.automacoes() }),
  });

  const executar = (automacao: Automacao, c: Comando, desfazendo = false): void => {
    if (comando.isPending) return;
    // Excluir não tem volta: antes, a confirmação com o nome digitado.
    if (c === 'excluir' && !excluindo) {
      comando.reset();
      definirExcluindo(automacao);
      return;
    }
    comando.mutate(
      { automacao, comando: c, desfazendo },
      c === 'excluir' ? { onSuccess: () => definirExcluindo(null) } : undefined,
    );
  };

  const emAndamento = comando.isPending ? comando.variables?.automacao.id : undefined;

  const contagem = useMemo(
    () => (emUso.data ? contagens(emUso.data.itens, arquivadas.data?.itens) : {}),
    [emUso.data, arquivadas.data],
  );

  const lista = filtro === 'arquivada' ? arquivadas : emUso;
  const doHistorico = historicoDe
    ? [...(emUso.data?.itens ?? []), ...(arquivadas.data?.itens ?? [])].find(
        (a) => a.id === historicoDe,
      )
    : undefined;
  const soArquivadas = emUso.data?.itens.length === 0 && (arquivadas.data?.itens.length ?? 0) > 0;
  const organizacao = me?.tenant.nome;
  const quantasArquivadas = arquivadas.data?.itens.length ?? 0;
  // `automacao × criar`: quem tem monta alguma coisa.
  const podeCriar = podeNaSessao(quem, 'automacao', 'criar');
  const quantosModelos = modelos.data
    ? modelosOferecidos(modelos.data.itens, quem).length
    : undefined;

  return (
    <div className="lista-de-automacoes">
      <CabecalhoDeTela
        titulo="Automações"
        retrato={
          <span className="lista-de-automacoes__icone-do-titulo" aria-hidden="true">
            <Icone nome="automacao" />
          </span>
        }
        apoio={
          organizacao
            ? `O que a ${organizacao} faz sozinha: responde, lembra e acompanha, enquanto a equipe cuida do resto.`
            : 'O que a sua empresa faz sozinha: responde, lembra e acompanha, enquanto a equipe cuida do resto.'
        }
        {...(pagina === 'lista' && (podeCriar || aoVerExecucoes || quantasArquivadas > 0)
          ? {
              acoes: (
                <>
                  {quantasArquivadas > 0 && (
                    <Botao
                      variante="secundario"
                      // O filtro da lista também se chama "Arquivadas": este abre o painel.
                      aria-label={`Abrir as automações arquivadas (${quantasArquivadas})`}
                      onClick={() => {
                        definirFalha(null);
                        definirRecado(null);
                        definirVendoArquivadas(true);
                      }}
                    >
                      <Icone nome="caixa" />
                      Arquivadas
                      <span className="lista-de-automacoes__contagem">{quantasArquivadas}</span>
                    </Botao>
                  )}
                  {aoVerExecucoes && (
                    <Botao variante="secundario" onClick={() => aoVerExecucoes(null, false)}>
                      <Icone nome="painel" />
                      Ver execuções
                    </Botao>
                  )}
                  {podeCriar && (
                    <Botao variante="primario" onClick={() => aoNavegar('modelos')}>
                      <Icone nome="criar" />
                      Nova automação
                    </Botao>
                  )}
                </>
              ),
            }
          : {})}
      />

      {/* O que vem abaixo do cabeçalho, no mesmo recuo dele: o `main` da área é
          cheio (o editor do mapa ocupa tudo), e sem isto a tela encostava no menu. */}
      <div className="lista-de-automacoes__corpo">
        {emUso.data && emUso.data.itens.length > 0 && (
          <Numeros
            emUso={emUso.data.itens}
            {...(aoVerExecucoes ? { aoVerFalhas: () => aoVerExecucoes(null, true) } : {})}
          />
        )}

        <Segmentado
          rotulo="Automações"
          como="abas"
          aparencia="sublinhado"
          idDasAbas="automacoes"
          opcoes={[
            {
              valor: 'lista',
              rotulo: 'Minhas automações',
              icone: 'automacao',
              ...(emUso.data ? { contagem: emUso.data.itens.length } : {}),
            },
            {
              valor: 'modelos',
              rotulo: 'Modelos prontos',
              icone: 'estrela',
              ...(quantosModelos !== undefined ? { contagem: quantosModelos } : {}),
            },
          ]}
          escolhida={pagina}
          aoEscolher={aoNavegar}
        />

        <div
          role="tabpanel"
          id={idDoPainel('automacoes', pagina)}
          aria-labelledby={`automacoes-${pagina}`}
          className="lista-de-automacoes__painel"
        >
          {pagina === 'modelos' ? (
            <GaleriaDeModelos
              quem={quem}
              {...(aoAbrirMapa
                ? { aoCriadoDoZero: (id: string) => aoAbrirMapa(id, null, true) }
                : {})}
              aoCriado={(nome) => {
                definirRecado({
                  texto: `Rascunho ${nome} criado. Ele ainda não roda: mande para teste e ative quando estiver pronto.`,
                });
                aoNavegar('lista');
                // O botão "Usar" sai da tela com a galeria: o foco vai para a aba
                // da lista, onde a automação nova está.
                window.setTimeout(() => document.getElementById('automacoes-lista')?.focus(), 0);
              }}
            />
          ) : (
            <>
              {falha && (
                <Faixa
                  tom="bloqueado"
                  acao={{ rotulo: 'Fechar', aoClicar: () => definirFalha(null) }}
                >
                  {falha}
                </Faixa>
              )}

              <Estado consulta={emUso} forma="lista" linhas={5}>
                {(dados) =>
                  // Vazio só com as duas listas lidas: a das arquivadas ainda chegando
                  // não é "nenhuma automação".
                  dados.itens.length === 0 &&
                  arquivadas.isSuccess &&
                  arquivadas.data.itens.length === 0 ? (
                    <Vazio
                      titulo="Nenhuma automação ainda"
                      porque="Comece por um modelo pronto: você ajusta tudo antes de ligar, e nada roda sem alguém ativar."
                      {...(podeCriar
                        ? {
                            acao: {
                              rotulo: 'Ver modelos prontos',
                              aoClicar: () => aoNavegar('modelos'),
                            },
                          }
                        : {})}
                    />
                  ) : (
                    <>
                      <div className="lista-de-automacoes__filtros">
                        {/* Seis recortes: acima de quatro, o `Segmentado` não é a peça. */}
                        <div
                          className="lista-de-automacoes__situacoes"
                          role="group"
                          aria-label="Filtrar por situação"
                        >
                          {/* "Arquivadas" só aparece quando há alguma: um recorte vazio
                            é um clique que não leva a nada. Escolhida, fica. */}
                          {FILTROS.filter(
                            (f) =>
                              f.valor !== 'arquivada' ||
                              filtro === 'arquivada' ||
                              (contagem.arquivada ?? 0) > 0,
                          ).map((f) => (
                            <FiltroPilula
                              key={f.valor}
                              ligado={filtro === f.valor}
                              aoAlternar={() => definirFiltro(f.valor)}
                              {...(contagem[f.valor] !== undefined
                                ? { contagem: contagem[f.valor] }
                                : {})}
                            >
                              {f.rotulo}
                            </FiltroPilula>
                          ))}
                        </div>
                        <div className="lista-de-automacoes__busca">
                          <Campo
                            type="search"
                            icone="buscar"
                            rotuloOculto="Buscar automação"
                            placeholder="Buscar pelo nome"
                            value={busca}
                            onChange={(e) => definirBusca(e.target.value)}
                          />
                        </div>
                      </div>

                      <Estado consulta={lista} forma="lista" linhas={3}>
                        {(dados) => {
                          const itens = filtrar(dados.itens, filtro, busca, nomeDe);
                          if (itens.length === 0) {
                            return (
                              <NadaEncontrado
                                filtro={filtro}
                                busca={busca}
                                soArquivadas={soArquivadas}
                                aoLimpar={() => {
                                  definirFiltro('todas');
                                  definirBusca('');
                                }}
                                aoVerArquivadas={() => {
                                  definirFiltro('arquivada');
                                  definirBusca('');
                                }}
                              />
                            );
                          }
                          return (
                            <>
                              <ul className="lista-de-automacoes__itens" aria-label="Automações">
                                {itens.map((a) => (
                                  <Linha
                                    key={a.id}
                                    automacao={a}
                                    nome={nomeDe(a)}
                                    origem={origemDaAutomacao(a, modelos.data)}
                                    resumo={resumoDoGatilho(a, blocos.data)}
                                    quem={quem}
                                    mudando={emAndamento === a.id}
                                    travado={comando.isPending}
                                    aoComandar={(c) => executar(a, c)}
                                    aoVerHistorico={() => definirHistoricoDe(a.id)}
                                    {...(podeEditarMapa(a, quem)
                                      ? { aoRenomear: () => definirRenomeando(a) }
                                      : {})}
                                    {...(aoAbrirMapa
                                      ? { aoEditar: () => aoAbrirMapa(a.id, null) }
                                      : {})}
                                    {...(aoVerExecucoes
                                      ? { aoVerExecucoes: () => aoVerExecucoes(a.id, false) }
                                      : {})}
                                  />
                                ))}
                              </ul>
                              <p className="lista-de-automacoes__total">
                                Mostrando {itens.length} de {dados.itens.length}{' '}
                                {dados.itens.length === 1 ? 'automação' : 'automações'}
                              </p>
                            </>
                          );
                        }}
                      </Estado>
                    </>
                  )
                }
              </Estado>
            </>
          )}
        </div>
      </div>

      {excluindo && (
        <ExcluirAutomacao
          nome={nomeDe(excluindo)}
          excluindo={comando.isPending}
          falha={comando.isError ? falha : null}
          aoConfirmar={() => executar(excluindo, 'excluir')}
          aoFechar={() => {
            if (!comando.isPending) definirExcluindo(null);
          }}
        />
      )}

      {vendoArquivadas && (
        <AutomacoesArquivadas
          arquivadas={arquivadas}
          quem={quem}
          nomeDe={nomeDe}
          resumoDe={(a) => resumoDoGatilho(a, blocos.data)}
          emAndamento={emAndamento}
          recado={recado?.texto ?? null}
          falha={falha}
          aoDesarquivar={(a) => executar(a, 'desarquivar')}
          aoFechar={() => definirVendoArquivadas(false)}
        />
      )}
      {renomeando && (
        <RenomearAutomacao
          automacao={renomeando}
          aoFechar={() => definirRenomeando(null)}
          aoSalvo={(nome) => {
            definirRenomeando(null);
            definirRecado({ texto: `Nome salvo: ${nome}.` });
          }}
        />
      )}
      {doHistorico && (
        <HistoricoDaAutomacao
          automacao={doHistorico}
          nome={nomeDe(doHistorico)}
          quem={quem}
          meuId={me?.usuario.id ?? null}
          aoFechar={() => definirHistoricoDe(null)}
          {...(aoAbrirMapa
            ? { aoVerVersao: (v: number | null) => aoAbrirMapa(doHistorico.id, v) }
            : {})}
        />
      )}

      {/* A região viva existe desde o começo: a que nasce junto com o texto, o
          leitor de tela não anuncia. */}
      <div className="lista-de-automacoes__recados" role="status" aria-live="polite">
        {recado && (
          <p className="lista-de-automacoes__recado">
            <span>{recado.texto}</span>
            {recado.desfazer && (
              <button
                type="button"
                className="lista-de-automacoes__desfazer"
                onClick={() => {
                  const d = recado.desfazer!;
                  executar(d.automacao, d.comando, true);
                }}
              >
                Desfazer
              </button>
            )}
            {recado.desfazer && (
              <button
                type="button"
                className="lista-de-automacoes__desfazer"
                aria-label="Fechar o aviso"
                onClick={() => definirRecado(null)}
              >
                <Icone nome="fechar" />
              </button>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Os quatro números. "Ligadas agora" sai da lista; os outros três, do painel
 * das últimas 24 h, que pode falhar sozinho: aí os três dizem que não chegaram,
 * e a lista continua.
 */
function Numeros({
  emUso,
  aoVerFalhas,
}: {
  emUso: readonly Automacao[];
  aoVerFalhas?: () => void;
}): ReactNode {
  const painel = usePainelDasAutomacoes('24h');
  const ligadas = emUso.filter((a) => a.situacao === 'ativa').length;
  const n = painel.data ? numerosDoPainel(painel.data) : null;
  const semPainel = painel.isError && !painel.data;
  // Enquanto o painel não chega, reticências: o travessão não é texto de tela.
  const valor = (v: number | undefined, sufixo = ''): string =>
    v === undefined ? '…' : `${v.toLocaleString('pt-BR')}${sufixo}`;
  const apoio = (pronto: string): string =>
    semPainel ? 'não chegou agora' : n ? pronto : 'carregando';

  return (
    <section className="lista-de-automacoes__numeros" aria-label="Números das automações">
      <Numero
        tom="roxo"
        icone="automacao"
        rotulo="Ligadas agora"
        valor={valor(ligadas)}
        apoio={`de ${emUso.length} ${emUso.length === 1 ? 'automação' : 'automações'}`}
      />
      <Numero
        icone="repetir"
        rotulo="Execuções em 24 h"
        valor={valor(n?.total)}
        apoio={apoio(
          n && n.ignoradas > 0
            ? `${valor(n.ignoradas)} sem a condição para rodar`
            : 'todas rodaram até o fim',
        )}
      />
      <Numero
        tom="ok"
        icone="check"
        rotulo="Deram certo"
        valor={valor(n?.taxa, '%')}
        apoio={apoio(`${valor(n?.concluidas)} ${n?.concluidas === 1 ? 'concluída' : 'concluídas'}`)}
      />
      <Numero
        tom={n && n.falhas > 0 ? 'erro' : undefined}
        icone="alerta"
        rotulo="Falharam em 24 h"
        valor={valor(n?.falhas)}
        apoio={
          // Com o link, ele já é o apoio: "Ver o que falhou" diz o que fazer.
          aoVerFalhas && n && n.falhas > 0
            ? ''
            : apoio(n && n.falhas > 0 ? 'cada uma diz o porquê' : 'nenhuma falha')
        }
        {...(aoVerFalhas && n && n.falhas > 0
          ? { acao: { rotulo: 'Ver o que falhou', aoClicar: aoVerFalhas } }
          : {})}
      />
      {/* A região existe antes do aviso, para o leitor de tela anunciar quando ele chega. */}
      <div className="lista-de-automacoes__numeros-falha" aria-live="polite">
        {semPainel && (
          <Faixa
            tom="defasado"
            acao={{ rotulo: 'Tentar de novo', aoClicar: () => void painel.refetch() }}
          >
            Os números das últimas 24 h não chegaram agora. A lista abaixo está certa.
          </Faixa>
        )}
      </div>
    </section>
  );
}

function Numero({
  tom,
  icone,
  rotulo,
  valor,
  apoio,
  acao,
}: {
  tom?: 'roxo' | 'ok' | 'erro' | undefined;
  icone: 'automacao' | 'repetir' | 'check' | 'alerta';
  rotulo: string;
  valor: string;
  apoio: string;
  acao?: { rotulo: string; aoClicar: () => void };
}): ReactNode {
  return (
    <div
      className={
        tom
          ? `lista-de-automacoes__numero lista-de-automacoes__numero--${tom}`
          : 'lista-de-automacoes__numero'
      }
    >
      <p className="lista-de-automacoes__numero-topo">
        <span className="lista-de-automacoes__numero-icone" aria-hidden="true">
          <Icone nome={icone} />
        </span>
        {rotulo}
      </p>
      <p className="lista-de-automacoes__numero-valor">{valor}</p>
      {apoio && <p className="lista-de-automacoes__numero-apoio">{apoio}</p>}
      {acao && (
        <button type="button" className="lista-de-automacoes__numero-acao" onClick={acao.aoClicar}>
          {acao.rotulo}
        </button>
      )}
    </div>
  );
}

/** O ícone de cada comando no menu "…": o rótulo diz o que ele faz. */
const ICONE_DO_COMANDO: Record<Comando, NomeDoIcone> = {
  enviar: 'enviar',
  voltar: 'voltar',
  ativar: 'automacao',
  pausar: 'pausa',
  arquivar: 'caixa',
  desarquivar: 'repetir',
  excluir: 'lixeira',
};

function Linha({
  automacao: a,
  nome,
  origem,
  resumo,
  quem,
  mudando,
  travado,
  aoComandar,
  aoVerHistorico,
  aoEditar,
  aoRenomear,
  aoVerExecucoes: verExecucoes,
}: {
  automacao: Automacao;
  nome: string;
  origem: string;
  resumo: string;
  quem: QuemNaTela | null;
  mudando: boolean;
  travado: boolean;
  aoComandar: (c: Comando) => void;
  aoVerHistorico: () => void;
  aoEditar?: () => void;
  /** Só para quem monta aquele tipo de automação. */
  aoRenomear?: () => void;
  aoVerExecucoes?: () => void;
}): ReactNode {
  const situacao = SITUACAO[a.situacao];
  const interruptor = temInterruptor(a, quem);
  // O que o interruptor faz não se repete no menu.
  const doMenu = comandosDe(a, quem).filter(
    (c) => !(interruptor && (c === 'ativar' || c === 'pausar')),
  );
  // O histórico é de quem vê a lista; os comandos, de quem pode cada um.
  const itens: ItemMenu[] = [
    ...(aoEditar
      ? [
          podeEditarMapa(a, quem)
            ? { rotulo: 'Editar', icone: 'lapis' as const, aoClicar: aoEditar }
            : { rotulo: 'Ver o mapa', icone: 'olho' as const, aoClicar: aoEditar },
        ]
      : []),
    ...(aoRenomear
      ? [{ rotulo: 'Renomear', icone: 'etiqueta' as const, aoClicar: aoRenomear }]
      : []),
    { rotulo: 'Histórico de versões', icone: 'relogio' as const, aoClicar: aoVerHistorico },
    ...(verExecucoes
      ? [{ rotulo: 'Ver execuções', icone: 'painel' as const, aoClicar: verExecucoes }]
      : []),
    ...doMenu.map((c, i) => ({
      rotulo: ROTULO_DO_COMANDO[c],
      icone: ICONE_DO_COMANDO[c],
      aoClicar: () => aoComandar(c),
      ...(c === 'excluir' ? { perigo: true } : {}),
      ...(i === 0 ? { separadorAntes: true } : {}),
      ...(travado ? { desabilitado: true, motivo: 'Espere o comando anterior terminar.' } : {}),
    })),
  ];
  const noAr = a.situacao === 'ativa';
  const rascunho = a.situacao === 'rascunho';
  // O rascunho não tem interruptor: o que se faz com ele é continuar montando.
  const continuar = rascunho && aoEditar && podeEditarMapa(a, quem);

  return (
    <li className="lista-de-automacoes__item" data-situacao={a.situacao}>
      <span className="lista-de-automacoes__tipo" aria-hidden="true">
        <Icone nome={iconeDaAutomacao(a)} />
      </span>

      <div className="lista-de-automacoes__principal">
        <p className="lista-de-automacoes__linha-do-nome">
          {aoEditar ? (
            <button type="button" className="lista-de-automacoes__nome" onClick={aoEditar}>
              {nome}
            </button>
          ) : (
            <span className="lista-de-automacoes__nome">{nome}</span>
          )}
          {/* O rascunho nunca foi publicado: o número da versão ainda não vale nada. */}
          {rascunho ? (
            <span className="lista-de-automacoes__versao" data-publicada="nao">
              não publicada
            </span>
          ) : (
            <span
              className="lista-de-automacoes__versao"
              title={noAr ? `A v${a.versao} é a que está no ar` : `Versão ${a.versao}`}
            >
              v{a.versao}
            </span>
          )}
        </p>
        <p className="lista-de-automacoes__resumo">{resumo}</p>
        <p className="lista-de-automacoes__origem">
          {origem}
          {a.usa_sql ? ' · lê o banco da empresa' : ''}
        </p>
      </div>

      <span className="lista-de-automacoes__situacao">
        <Selo tom={situacao.tom} vivo={situacao.vivo}>
          {situacao.icone && <Icone nome={situacao.icone} />}
          {situacao.rotulo}
        </Selo>
      </span>

      <div className="lista-de-automacoes__ligar">
        {interruptor && (
          <Interruptor
            ligado={a.situacao === 'ativa'}
            rotulo={a.situacao === 'ativa' ? 'Ligada' : 'Desligada'}
            contexto={nome}
            aoAlternar={() => aoComandar(a.situacao === 'ativa' ? 'pausar' : 'ativar')}
            {...(travado
              ? {
                  desabilitado: true,
                  motivo: mudando ? 'Mudando agora…' : 'Espere o comando anterior terminar.',
                }
              : {})}
          />
        )}
        {continuar && (
          <Botao variante="secundario" onClick={aoEditar} aria-label={`Continuar ${nome}`}>
            <Icone nome="lapis" />
            Continuar
          </Botao>
        )}
      </div>

      <div className="lista-de-automacoes__mais">
        <MenuAcoes itens={itens} rotulo={`Mais ações de ${nome}`} />
      </div>
    </li>
  );
}

function NadaEncontrado({
  filtro,
  busca,
  soArquivadas,
  aoLimpar,
  aoVerArquivadas,
}: {
  filtro: Filtro;
  busca: string;
  soArquivadas: boolean;
  aoLimpar: () => void;
  aoVerArquivadas: () => void;
}): ReactNode {
  const termo = busca.trim();
  // Nada a limpar: só há arquivadas, e "Ver todas" não levaria a lugar nenhum.
  if (filtro === 'todas' && termo === '') {
    return (
      <p className="lista-de-automacoes__nada">
        {soArquivadas
          ? 'Nenhuma automação em uso. As que existem estão arquivadas. '
          : 'Nenhuma automação. '}
        {soArquivadas && (
          <Botao variante="fantasma" miudo onClick={aoVerArquivadas}>
            Ver arquivadas
          </Botao>
        )}
      </p>
    );
  }
  const rotulo = FILTROS.find((f) => f.valor === filtro)?.rotulo.toLowerCase();
  const onde = filtro === 'todas' ? '' : ` em “${rotulo}”`;
  const com = termo ? ` com “${termo}”` : '';
  return (
    <p className="lista-de-automacoes__nada">
      Nenhuma automação{onde}
      {com}.{' '}
      <Botao variante="fantasma" miudo onClick={aoLimpar}>
        Ver todas
      </Botao>
    </p>
  );
}
